import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Wallet } from "lucide-react";
import { fetchOrgExtraJobs, type CleaningExtraJob } from "@/lib/extraPaidWork";

interface ExtraPaidJobsBannerProps {
  orgId: string | null;
  userId: string | null | undefined;
}

export function ExtraPaidJobsBanner({ orgId, userId }: ExtraPaidJobsBannerProps) {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<CleaningExtraJob[]>([]);

  useEffect(() => {
    if (!orgId || !userId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const rows = await fetchOrgExtraJobs(orgId);
        if (!cancelled) {
          setJobs(
            rows.filter(
              (job) =>
                job.assignedStaffId === userId &&
                job.taskStatus !== "done" &&
                job.taskStatus !== "cancelled" &&
                job.approvalStatus !== "rejected"
            )
          );
        }
      } catch (error) {
        console.warn("[ExtraPaidJobsBanner]", error);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [orgId, userId]);

  if (jobs.length === 0) return null;

  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <Wallet className="h-4 w-4 text-amber-600" />
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Prace ekstra płatne
        </h2>
      </div>
      <div className="space-y-2">
        {jobs.map((job) => (
          <Card
            key={job.id}
            className="cursor-pointer border-amber-200 dark:border-amber-900/40"
            onClick={() => navigate(`/property/${job.locationId}`)}
          >
            <CardContent className="py-3 flex items-center justify-between gap-3">
              <div>
                <p className="font-medium text-sm">{job.title}</p>
                <p className="text-xs text-muted-foreground">{job.locationAddress || "Obiekt"}</p>
              </div>
              <Badge variant={job.taskStatus === "in_progress" ? "default" : "secondary"}>
                {job.taskStatus === "in_progress" ? "W trakcie" : "Do wykonania"}
              </Badge>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}
